/**
 * The card every view is built from. `title` renders the uppercase caption
 * the monolith used on every panel; `actions` is the right-aligned slot for
 * per-panel controls (period switches, mode toggles) that used to be
 * hand-positioned with inline flex in each view.
 */
export function Panel({ title, actions, children, className = '', bodyClassName = '' }) {
  return (
    <section className={`panel ${className}`}>
      {(title || actions) && (
        <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {title ? <h3 className="panel-title !mb-0">{title}</h3> : <span />}
          {actions ? <div className="flex flex-wrap items-center gap-1.5">{actions}</div> : null}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export function EmptyState({ children }) {
  return <div className="py-6 text-center text-[12px] text-dim">{children}</div>;
}

/** Query error surfaced in place, rather than an empty panel that looks
 *  like "no data" when it actually means "the request failed".
 *
 *  `role="alert"` because most of these appear *after* the screen has
 *  settled — a reassignment rejected, a bulk cancel that half-failed. Red
 *  text below the fold announces itself to nobody. */
export function ErrorState({ error, children }) {
  return (
    <div
      role="alert"
      className="rounded-[10px] border border-red/25 bg-red/8 px-3 py-2 text-[12px] text-red-ink"
    >
      {children ?? error?.message ?? 'Error'}
    </div>
  );
}

export function SkeletonRows({ rows = 3, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-[9px] bg-white/5" />
      ))}
    </div>
  );
}
